import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Facebook, Twitter, Instagram, Linkedin, Youtube, Sparkles, Check, Send, Phone, Mail, MapPin, Leaf, Globe } from 'lucide-react';
import { useSiteSettings } from '../../hooks/useSiteSettings';
import { supabase } from '../../lib/supabase';

const PRODUCT_LINKS = [
  { name: 'SUNU', path: '/products/sunu' },
  { name: 'TERANGA+', path: '/products/teranga-plus' },
  { name: 'OMEGA', path: '/products/omega' },
  { name: 'Stations Proxy Power', path: '/stations' },
];
const COMPANY_LINKS = [
  { name: 'À propos', path: '/about' },
  { name: 'Carrières', path: '/careers' },
  { name: 'Blog', path: '/blog' },
  { name: 'Investir', path: '/invest' },
];
const SUPPORT_LINKS = [
  { name: 'Contact', path: '/contact' },
  { name: 'Services & SAV', path: '/services' },
  { name: 'Formation', path: '/training' },
  { name: 'Financement (PAYG)', path: '/pay-as-you-go' },
];
const LEGAL_LINKS = [
  { name: 'Mentions légales', path: '/legal' },
  { name: 'Confidentialité', path: '/privacy' },
  { name: 'CGU', path: '/terms' },
  { name: 'Cookies', path: '/cookies' },
];
type IconType = React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
const SOCIAL_ICONS: Record<string, IconType> = { Facebook, Twitter, Instagram, LinkedIn: Linkedin, Linkedin, YouTube: Youtube, Youtube };
const DEFAULT_SOCIALS = [
  { label: 'Facebook', url: 'https://facebook.com/proxyemobility' },
  { label: 'Twitter', url: 'https://twitter.com/proxyemobility' },
  { label: 'Instagram', url: 'https://instagram.com/proxyemobility' },
  { label: 'LinkedIn', url: 'https://linkedin.com/company/proxyemobility' },
  { label: 'YouTube', url: 'https://youtube.com/@proxyemobility' },
];
const DEFAULT_COLUMNS = [
  { title: 'Produits', links: PRODUCT_LINKS },
  { title: 'Entreprise', links: COMPANY_LINKS },
  { title: 'Support', links: SUPPORT_LINKS },
];

const FooterModern: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const settings = useSiteSettings();
  const tagline = settings?.tagline || 'Leader de la mobilité électrique en Afrique. Motos, stations solaires, financement flexible — pour une mobilité accessible et durable.';
  const contactEmail = settings?.email || 'hello@proxyemobility.com';
  const footerAddress = settings?.footer_address || 'Showroom Dakar\nPetite Côte · Sénégal';
  const footerNote = settings?.footer_note || 'Made in Senegal';
  const socials = settings?.socials?.length ? settings.socials : DEFAULT_SOCIALS;
  const columns = settings?.footer_columns?.length ? settings.footer_columns : DEFAULT_COLUMNS;
  const legal = settings?.legal_links?.length ? settings.legal_links : LEGAL_LINKS;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    try {
      if (!supabase) throw new Error('offline');
      const { error } = await supabase.from('newsletter_subscribers').insert({ email: email.trim().toLowerCase(), source: 'footer' });
      if (error) {
        if (error.code === '23505') {
          toast.success('Vous êtes déjà inscrit, merci !');
          setDone(true);
          setEmail('');
        } else throw error;
      } else {
        toast.success('Inscription confirmée. À très vite !');
        setDone(true);
        setEmail('');
      }
    } catch (err) {
      console.error(err);
      toast.error('Erreur. Réessayez plus tard.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="relative overflow-hidden bg-neutral-950 text-white pb-[calc(env(safe-area-inset-bottom)+4.5rem)] md:pb-0">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-[5%] h-96 w-96 rounded-full bg-primary-500/[0.08] blur-3xl" />
        <div className="absolute -bottom-40 right-[8%] h-[28rem] w-[28rem] rounded-full bg-primary-400/[0.06] blur-3xl" />
      </div>
      <div className="container relative mx-auto py-14 sm:py-16 lg:py-20">
        <div className="mb-12 grid grid-cols-1 gap-10 border-b border-white/10 pb-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5">
            <Link to="/" className="inline-block">
              <img src="/proxy-logo.png" alt="Proxy Emobility" className="h-10 brightness-0 invert" />
            </Link>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-white/60 sm:text-base">{tagline}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {socials.map((s) => {
                const Icon = SOCIAL_ICONS[s.label] || Globe;
                return (
                  <a
                    key={s.label}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 bg-white/5 text-white/70 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-400/50 hover:bg-primary-500/10 hover:text-primary-400"
                  >
                    <Icon size={16} strokeWidth={2.2} />
                  </a>
                );
              })}
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm sm:p-8">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary-400/30 bg-primary-500/10 px-3 py-1">
                <Sparkles className="h-3 w-3 text-primary-400" strokeWidth={2.5} />
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-primary-400">Newsletter</span>
              </div>
              <h3 className="mb-2 text-xl font-black tracking-tight text-white sm:text-2xl">
                Restez à la pointe de la <span className="text-primary-400">mobilité électrique</span>
              </h3>
              <p className="mb-5 text-sm text-white/60">Nouveautés produits, conseils d'experts et offres exclusives — une fois par mois.</p>
              <form className="flex flex-col gap-3 sm:flex-row" onSubmit={handleSubmit}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Votre adresse email"
                  className="flex-1 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/40 backdrop-blur-sm focus:border-primary-400/40 focus:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary-400/20"
                  aria-label="Votre adresse email"
                  required
                  disabled={submitting || done}
                />
                <button
                  type="submit"
                  disabled={submitting || done}
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-bold text-neutral-900 shadow-[0_10px_30px_-12px_rgba(255,255,255,0.3)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary-400 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {done ? (
                    <>
                      <Check size={14} /> Inscrit
                    </>
                  ) : submitting ? (
                    'Envoi...'
                  ) : (
                    <>
                      S'inscrire <Send size={14} className="transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 sm:grid-cols-2 lg:grid-cols-5 lg:gap-6">
          <div className="col-span-2 sm:col-span-2 lg:col-span-2">
            <h4 className="mb-4 text-[11px] font-black uppercase tracking-[0.22em] text-white/40">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a href={`mailto:${contactEmail}`} className="group flex items-start gap-3 text-white/70 transition-colors hover:text-white">
                  <Mail size={14} className="mt-0.5 shrink-0 text-primary-400" strokeWidth={2.5} />
                  <span className="break-all">{contactEmail}</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-white/70">
                  <MapPin size={14} className="mt-0.5 shrink-0 text-primary-400" strokeWidth={2.5} />
                  <span>
                    {footerAddress.split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <br />}
                        {line}
                      </React.Fragment>
                    ))}
                  </span>
                </div>
              </li>
            </ul>
          </div>
          {columns.map((col) => (
            <div key={col.title}>
              <h4 className="mb-4 text-[11px] font-black uppercase tracking-[0.22em] text-white/40">{col.title}</h4>
              <ul className="space-y-2.5 text-sm">
                {col.links.map((l) => (
                  <li key={l.name}>
                    <Link to={l.path} className="inline-block text-white/70 transition-colors hover:text-primary-400">
                      {l.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-start gap-6 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-3 text-xs text-white/50 sm:flex-row sm:items-center sm:gap-6">
            <span>© {new Date().getFullYear()} Proxy Emobility. Tous droits réservés.</span>
            <span className="hidden sm:inline">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Leaf size={12} className="text-primary-400" strokeWidth={2.5} />
              {footerNote}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs">
            {legal.map((l) => (
              <Link key={l.name} to={l.path} className="text-white/50 transition-colors hover:text-white">
                {l.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterModern;
